import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Icon from "./Icon";
import { INGREDIENT_GROUPS } from "../data/ingredients";

// Grouped ingredient accordion (light style) for Product & Ingredients pages.
export const IngredientGroups = ({ defaultOpen = 0, showAmount = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="space-y-4" data-testid="ingredient-groups">
      {INGREDIENT_GROUPS.map((g, i) => {
        const isOpen = open === i;
        return (
          <motion.div
            key={g.category}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: i * 0.04 }}
            className={`rounded-3xl border bg-white overflow-hidden transition-all duration-300 ${
              isOpen ? "border-royal/30 shadow-[0_12px_40px_rgb(30,58,138,0.10)]" : "border-slate-100 hover:border-slate-200"
            }`}
          >
            <button
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="w-full flex items-center gap-4 p-5 md:p-6 text-left"
              data-testid={`ingredient-group-${i}`}
            >
              <span className={`flex items-center justify-center w-12 h-12 rounded-2xl shrink-0 ${g.bg} ${g.fg}`}>
                <Icon name={g.icon} className="w-6 h-6" />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-lg font-semibold text-navy-900 flex items-center gap-2 flex-wrap">
                  {g.category}
                  {g.note && (
                    <span className="text-[10px] font-bold tracking-wide uppercase bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                      {g.note}
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">{g.ingredients.length} active ingredients</p>
              </div>
              <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                <ChevronDown className="w-5 h-5 text-slate-400" />
              </motion.span>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-5 md:px-6 pb-6 pt-1">
                    <div className={`h-px w-full mb-3 bg-gradient-to-r ${g.line} to-transparent`} />
                    <ul>
                      {g.ingredients.map((ing, j) => (
                        <motion.li
                          key={ing.name}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: j * 0.025 }}
                          className="group flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5 px-3 rounded-xl hover:bg-slate-50 transition-colors"
                        >
                          <span className="font-medium text-navy-900 text-sm">{ing.name}</span>
                          {showAmount && (
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${g.bg} ${g.fg} whitespace-nowrap`}>
                              {ing.amount}
                            </span>
                          )}
                          <span className="text-sm text-slate-400 w-full sm:w-auto sm:flex-1">{ing.benefit}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

export default IngredientGroups;
